'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { useUpdateJournal } from '@/entries'
import type { Entry } from '@/document'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WeeklyReflectionCardProps {
  entry: Entry
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Split the entry content into narrative (before last ---) and user reflection (after). */
function splitContent(content: string): {
  narrative: string
  reflection: string
} {
  const separatorIndex = content.lastIndexOf('\n\n---\n\n')
  if (separatorIndex === -1) {
    return { narrative: content, reflection: '' }
  }

  const afterSeparator = content.slice(separatorIndex + '\n\n---\n\n'.length)
  // The prompt line is "How are you feeling about this week?"
  // User reflection is everything after that line
  const promptLine = 'How are you feeling about this week?'
  const promptIndex = afterSeparator.indexOf(promptLine)
  if (promptIndex === -1) {
    return { narrative: content, reflection: '' }
  }

  const beforeSeparator = content.slice(0, separatorIndex)
  const afterPrompt = afterSeparator
    .slice(promptIndex + promptLine.length)
    .trim()

  return { narrative: beforeSeparator, reflection: afterPrompt }
}

/** Rebuild content with user reflection appended after the prompt. */
function rebuildContent(content: string, newReflection: string): string {
  const separatorIndex = content.lastIndexOf('\n\n---\n\n')
  if (separatorIndex === -1) {
    return content
  }

  const beforeSeparator = content.slice(0, separatorIndex)
  const reflectionSuffix = newReflection.trim()
    ? `\n\n${newReflection.trim()}`
    : ''

  return `${beforeSeparator}\n\n---\n\nHow are you feeling about this week?${reflectionSuffix}`
}

// ---------------------------------------------------------------------------
// Narrative renderer
// ---------------------------------------------------------------------------

function NarrativeContent({ text }: { text: string }) {
  const paragraphs = text.split('\n\n').filter(Boolean)

  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, i) => {
        // Check if it's a list (lines starting with -)
        const lines = paragraph.split('\n')
        if (lines.length > 1 && lines.some((l) => l.startsWith('- '))) {
          const intro = lines.filter((l) => !l.startsWith('- ')).join(' ')
          const items = lines
            .filter((l) => l.startsWith('- '))
            .map((l) => l.slice(2))
          return (
            <div key={i}>
              {intro && <p className="text-foreground/80 text-sm">{intro}</p>}
              <ul className="text-foreground/80 mt-1 list-inside list-disc space-y-0.5 text-sm">
                {items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            </div>
          )
        }
        return (
          <p key={i} className="text-foreground/80 text-sm">
            {paragraph}
          </p>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WeeklyReflectionCard({ entry }: WeeklyReflectionCardProps) {
  const updateJournal = useUpdateJournal()
  const { narrative, reflection } = splitContent(entry.content)
  const [localReflection, setLocalReflection] = useState(reflection)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveReflection = useCallback(
    (value: string) => {
      // Rebuilt from the entry this card was rendered with, which is the same document read the
      // narrative above came from. The narrative half is written once, at creation, and never
      // edited, so it is not a value two devices race over.
      updateJournal.mutate({
        id: entry.id,
        data: { content: rebuildContent(entry.content, value) },
      })
    },
    [updateJournal, entry.id, entry.content],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      setLocalReflection(value)

      // Debounce save
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        saveReflection(value)
      }, 500)
    },
    [saveReflection],
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <div
      className="border-border/30 bg-card/50 rounded-lg border px-5 py-4"
      data-testid="weekly-reflection-card"
      aria-label="Weekly reflection"
    >
      {/* Title */}
      <h3 className="text-foreground font-serif text-lg">{entry.title}</h3>

      {/* Auto-generated indicator */}
      <p className="text-muted-foreground/40 mt-0.5 text-xs">Auto-generated</p>

      {/* Narrative body */}
      <div className="mt-3">
        <NarrativeContent text={narrative} />
      </div>

      {/* Separator */}
      <hr className="border-border/30 my-4" />

      {/* Feeling prompt */}
      <p className="text-muted-foreground text-sm font-medium">
        How are you feeling about this week?
      </p>

      {/* Textarea */}
      <textarea
        className="border-border/30 focus:ring-accent/50 text-foreground placeholder:text-muted-foreground/40 mt-2 min-h-[80px] w-full resize-none rounded-md border bg-transparent px-3 py-2 text-sm focus:ring-1 focus:outline-none"
        placeholder="Write here, or leave blank -- it's your space."
        value={localReflection}
        onChange={handleChange}
        aria-label="Weekly reflection feeling"
        id="weekly-reflection-feeling"
      />
    </div>
  )
}
